import { QueryEntity } from '@datorama/akita';
import {
	CacheObservable,
	compareEntities,
	compareEntity,
	isEquals,
} from '@lycoris-nubila/ngx-lycoris';
import { isEqual, isNil, keyBy, reject, uniqBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { JobOffer } from 'job/job-offer';
import { Module } from 'module/module';
import { TaskRepositoryPort } from 'task/port/task-repository.port';
import { Task } from 'task/task';
import { TaskState, TaskStore } from 'task/task.store';

export class TaskRepositoryAdapter
	extends QueryEntity<TaskState>
	implements TaskRepositoryPort
{
	constructor(
		protected store: TaskStore,
		private modules$: (ids: string[]) => Observable<Module[]>,
		private jobOffers$: (ids: string[]) => Observable<JobOffer[]>
	) {
		super(store);
	}

	public observeUserDayTasks(
		userId: string,
		date: DateTime
	): Observable<Task[]> {
		return this.selectAll({
			filterBy: [
				(entity) => entity.dueDate >= date.startOf('day').toMillis(),
				(entity) => entity.dueDate <= date.endOf('day').toMillis(),
				(entity) =>
					entity.ownerId === userId || entity.participantsId.includes(userId),
			],
		}).pipe(
			distinctUntilChanged(isEqual),
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async deleteTask(id: string): Promise<void> {
		this.store.remove(id);
	}

	public observeUserPendingTasks(userId: string): Observable<Task[]> {
		return this.selectAll({
			filterBy: (entity) => isEquals(entity.ownerId, userId) && !entity.done,
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public observeUserOverdueTasks(userId: string): Observable<Task[]> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.ownerId, userId) &&
				entity.dueDate < Date.now() &&
				!entity.done,
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeContactTasks(contactId: string): Observable<Task[]> {
		return this.selectAll({
			filterBy: (entity) => isEquals(entity.contactId, contactId),
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeJobOfferTasks(jobOfferId: string): Observable<Task[]> {
		return this.selectAll({
			filterBy: (entity) => isEquals(entity.jobOfferId, jobOfferId),
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeTask(id: string): Observable<Task> {
		return this.selectEntity(id).pipe(
			map((document) =>
				this.store.asEntity(document, this.modules$, this.jobOffers$)
			),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public observeTasks(ids: string[]): Observable<Task[]> {
		return this.selectMany(ids).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.modules$, this.jobOffers$)
				)
			),
			distinctUntilChanged(compareEntities),
			map((tasks) => keyBy(tasks, 'id')),
			map((tasksMap) => ids.map((id) => tasksMap[id])),
			map((tasks) => reject(tasks, isNil)),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveTasks(entities: Task[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
